// src/contexts/DialogContext.js
//
// Styled replacement for window.confirm / window.alert.
//
//   const { confirm, alert } = useDialog();
//   if (!(await confirm({ title: 'Delete flight?', message: '…', confirmLabel: 'Delete', tone: 'danger' }))) return;
//   await alert({ title: 'Terms of Service', message: longText });

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';

const DialogContext = createContext(null);

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) throw new Error('useDialog must be used inside <DialogProvider>');
  return context;
};

const TONES = {
  danger: { icon: AlertTriangle, iconClass: 'text-red-600 bg-red-100', buttonClass: 'bg-red-600 hover:bg-red-700' },
  default: { icon: Info, iconClass: 'text-sky-600 bg-sky-100', buttonClass: 'bg-sky-600 hover:bg-sky-700' },
};

export const DialogProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);
  const confirmButtonRef = useRef(null);
  const cancelButtonRef = useRef(null);

  const open = useCallback((kind, options) => new Promise(resolve => {
    resolverRef.current = resolve;
    setDialog({ kind, ...options });
  }), []);

  const confirm = useCallback((options) => open('confirm', options), [open]);
  const alert = useCallback((options) => open('alert', options), [open]);

  const close = useCallback((result) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setDialog(null);
  }, []);

  useEffect(() => {
    if (!dialog) return;
    // Destructive dialogs start on Cancel so a reflexive Enter doesn't delete
    const initialFocus = dialog.kind === 'confirm' && dialog.tone === 'danger' ? cancelButtonRef : confirmButtonRef;
    initialFocus.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dialog, close]);

  const tone = TONES[dialog?.tone] || TONES.default;
  const Icon = tone.icon;

  return (
    <DialogContext.Provider value={{ confirm, alert }}>
      {children}
      {dialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
          onClick={() => close(false)}
        >
          <div
            role={dialog.kind === 'confirm' ? 'alertdialog' : 'dialog'}
            aria-modal="true"
            aria-labelledby="app-dialog-title"
            aria-describedby="app-dialog-message"
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 p-6 pb-4">
              <div className={`shrink-0 rounded-full p-2 ${tone.iconClass}`}>
                <Icon className="h-5 w-5" />
              </div>
              <h2 id="app-dialog-title" className="text-lg font-semibold text-gray-900 pt-1">
                {dialog.title}
              </h2>
            </div>
            {dialog.message && (
              <div id="app-dialog-message" className="px-6 text-sm text-gray-600 whitespace-pre-line overflow-y-auto">
                {dialog.message}
              </div>
            )}
            <div className="flex justify-end gap-3 p-6 pt-5">
              {dialog.kind === 'confirm' && (
                <button
                  type="button"
                  ref={cancelButtonRef}
                  onClick={() => close(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  {dialog.cancelLabel || 'Cancel'}
                </button>
              )}
              <button
                type="button"
                ref={confirmButtonRef}
                onClick={() => close(true)}
                className={`px-4 py-2 text-white rounded-md transition-colors ${tone.buttonClass}`}
              >
                {dialog.confirmLabel || (dialog.kind === 'confirm' ? 'Confirm' : 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
};
